import { FieldLimits } from '@repo/utils/src/fieldLimits';

import { IBillingConfig, ISubscription, IVoteType } from '@/types/models';
import { ApiResponse } from '@/types/types';
import api from '@/utils/api';

/** Mirrors App\Utils\EmbedWidgets: the catalog the embed builder renders itself from. */
export interface EmbedWidgetOption {
  label: string;
  type: 'select' | 'text' | 'color';
  default: string;
  values?: Record<string, string>;
  help?: string;
  max?: number;
}

export interface EmbedWidget {
  key: string;
  label: string;
  summary: string;
  mode: 'iframe' | 'trigger';
  min_height: number;
  options: Record<string, EmbedWidgetOption>;
}

export interface EmbedWidgetCatalog {
  loader_url: string;
  default: string;
  widgets: Record<string, EmbedWidget>;
}

export interface DashboardConfig {
  /** Keys alone: the name, description and preview SVG live in `@repo/utils/src/templatePreviews`. */
  templates: string[];
  vote_types: Record<string, IVoteType>;
  currencies: string[];
  subscription_types: Record<string, ISubscription>;
  stripe_publishable_key: string;
  flutterwave_public_key: string;
  google_client_id: string;
  /** Absent until the config request lands, so every reader guards it. */
  embed_widgets?: EmbedWidgetCatalog;
  /** Absent until the config request lands. */
  billing?: IBillingConfig;
  /** Absent until the config request lands, so a field without it simply has no cap yet. */
  field_limits?: FieldLimits;
}

type DashboardConfigRes = ApiResponse<DashboardConfig>;

export const getDashboardConfig = async () => {
  // ?v=BUILD_HASH busts cache per build; withCredentials:false keeps it public (no Set-Cookie) for CDN caching.
  // A dev server keeps one hash for days while the API changes under it, so dev never reuses a cached copy.
  const { data } = await api.get<DashboardConfigRes>('/api/v1/dashboard/config', {
    params: { v: import.meta.env.DEV ? Date.now() : BUILD_HASH },
    withCredentials: false,
  });

  return data.data;
};
